/**
 * UnidadesPage Component
 * 
 * Página de listagem e gerenciamento de unidades de medida.
 * Permite visualizar, criar, editar e excluir unidades.
 * 
 * Funcionalidades:
 * - Listagem de unidades com paginação
 * - Filtros de busca
 * - Modal de criação/edição
 * - Exclusão com confirmação
 */

import { useState, useEffect, useCallback } from 'react'
import { Edit, Trash2, Plus, Search, Ruler } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { unitService } from '../../../../services/erpApi'
import './UnidadesPage.css'

function UnidadesPage() {
  const [unidades, setUnidades] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingUnidade, setEditingUnidade] = useState(null)
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    active: true
  })
  const [errors, setErrors] = useState({})
  const [searchTerm, setSearchTerm] = useState('')

  const loadUnidades = useCallback(async () => {
    setLoading(true)
    try {
      const response = await unitService.getAll({ search: searchTerm })
      if (response.success) {
        setUnidades(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar unidades:', error)
    } finally {
      setLoading(false)
    }
  }, [searchTerm])

  useEffect(() => {
    loadUnidades()
  }, [loadUnidades])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        code: formData.code.toUpperCase(),
        name: formData.name,
        active: formData.active
      }

      let response
      if (editingUnidade) {
        response = await unitService.update(editingUnidade.id, dataToSend)
      } else {
        response = await unitService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadUnidades()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar unidade' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar unidade:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar unidade'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleEdit = (unidade) => {
    setEditingUnidade(unidade)
    setFormData({
      code: unidade.code || '',
      name: unidade.name || '',
      active: unidade.active !== undefined ? unidade.active : true
    })
    setModalOpen(true)
  }

  const handleDelete = async (unidade) => {
    if (window.confirm(`Deseja realmente excluir a unidade ${unidade.name}?`)) {
      try {
        const response = await unitService.delete(unidade.id)
        if (response.success) {
          loadUnidades()
        }
      } catch (error) {
        console.error('Erro ao excluir unidade:', error)
        alert('Erro ao excluir unidade')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      active: true
    })
    setEditingUnidade(null)
    setErrors({})
  }

  const columns = [
    { key: 'code', label: 'Código', sortable: true },
    { key: 'name', label: 'Nome', sortable: true },
    {
      key: 'active',
      label: 'Status',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDelete
    }
  ]

  return (
    <div className="unidades-page">
      <div className="page-header">
        <div>
          <h1>
            <Ruler size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Unidades de Medida
          </h1>
          <p>Gerencie unidades de medida de produtos</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm()
            setModalOpen(true)
          }}
        >
          <Plus size={18} />
          Nova Unidade
        </button>
      </div>

      <div className="page-filters">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Buscar unidades..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={unidades}
        loading={loading}
        actions={actions}
        emptyMessage="Nenhuma unidade encontrada"
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingUnidade ? 'Editar Unidade' : 'Nova Unidade'}
        size="small"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingUnidade ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Código"
            name="code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            error={errors.code}
            required
            placeholder="Ex: KG, L, UN, etc"
            maxLength={10}
          />

          <FormField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            required
            placeholder="Ex: Quilograma, Litro, Unidade, etc"
          />

          <FormField
            label="Status"
            name="active"
            type="select"
            value={formData.active ? 'true' : 'false'}
            onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
            options={[
              { value: 'true', label: 'Ativo' },
              { value: 'false', label: 'Inativo' }
            ]}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>
    </div>
  )
}

export default UnidadesPage

